import { useEffect, useState, useRef, useCallback } from "react";
import {
    Box,
    Typography,
    Card,
    CardContent,
    Chip,
    Fade,
    Button,
    ButtonGroup,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import ParallelSubNav from "./ParallelSubNav";

// Nos libellés d'étape arrivent en français depuis le backend
// (roundLabel() dans finals.js) — traduits ici pour l'affichage public
// anglophone.
const STAGE_TRANSLATIONS = {
    "Finale": "Final",
    "Petite finale (3e / 4e)": "Small Final",
    "Demi-finales": "Semifinals",
    "Quarts de finale": "1/4 Final",
    "1/8 de finale": "1/8 Final",
    "1/16 de finale": "1/16 Final",
};

function translateStage(label) {
    return STAGE_TRANSLATIONS[label] || label;
}

// Composant unique pour les deux routes publiques :
//   /parallel-live/:category          — vue live standard
//   /parallel-live/:category/speaker  — même vue, texte plus grand (speaker)
// Adapté d'une maquette fournie — repensé pour nos vraies routes/API
// (catégories libres type "women1"/"men1", pas juste women/men binaire ;
// /api/finals/:category déjà public ; nouvelle route publique dédiée pour
// lister les catégories disponibles, /api/parallel-categories/public).
export default function ParallelLive({ speakerMode = false }) {
    const { category } = useParams();
    const navigate = useNavigate();

    const [bracket, setBracket] = useState(null);
    const [categories, setCategories] = useState([]);
    const [lastWinners, setLastWinners] = useState({});
    const [roundCompletionTimes, setRoundCompletionTimes] = useState({});
    const [hasScrolled, setHasScrolled] = useState(false);
    const [highlightedDuel, setHighlightedDuel] = useState(null);

    const duelRefs = useRef({});

    const apiFetch = useCallback(async (url) => {
        try {
            const res = await fetch(url);
            return res.ok ? res.json() : null;
        } catch {
            return null;
        }
    }, []);

    // -------------------------
    // Liste des catégories disponibles (pour le sélecteur)
    // -------------------------
    useEffect(() => {
        apiFetch("/api/parallel-categories/public").then((data) => setCategories(data || []));
    }, [apiFetch]);

    // -------------------------
    // Trouver le duel suivant
    // -------------------------
    const getNextDuel = useCallback((bracket, currentId) => {
        if (!bracket?.rounds) return null;

        for (let r = 0; r < bracket.rounds.length; r++) {
            const round = bracket.rounds[r];

            for (let d = 0; d < round.length; d++) {
                const duel = round[d];

                if (duel.id === currentId) {
                    if (round[d + 1]) return round[d + 1];
                    if (bracket.rounds[r + 1]) return bracket.rounds[r + 1][0];
                    // Après le dernier tour (la finale), la petite finale
                    // est la suite logique s'il y en a une.
                    if (bracket.smallFinal) return bracket.smallFinal;
                    return null;
                }
            }
        }

        return null;
    }, []);

    // -------------------------
    // Chargement + détection gagnants
    // -------------------------
    useEffect(() => {
        const load = async () => {
            const data = await apiFetch(`/api/finals/${encodeURIComponent(category)}`);
            if (!data || !data.rounds) return;

            const newWinners = {};
            data.rounds.forEach((round) =>
                round.forEach((duel) => {
                    if (duel.winner) newWinners[duel.id] = duel.winner.name;
                })
            );
            if (data.smallFinal?.winner) {
                newWinners[data.smallFinal.id] = data.smallFinal.winner.name;
            }

            setLastWinners((prev) => {
                const updated = {};
                for (const id in newWinners) {
                    if (prev[id] !== newWinners[id]) updated[id] = true;
                }
                return updated;
            });

            setBracket(data);
        };

        load();
        const interval = setInterval(load, 3000);
        return () => clearInterval(interval);
    }, [category, apiFetch]);

    // -------------------------
    // Scroll automatique (une seule fois) — SPEAKER UNIQUEMENT. La vue
    // live normale reste statique, navigation manuelle par le spectateur.
    // -------------------------
    useEffect(() => {
        if (!speakerMode) return;
        if (!bracket?.rounds) return;
        if (hasScrolled) return;

        const allDuels = bracket.rounds.flat();
        const duelsWithWinner = allDuels.filter((d) => d.winner);

        if (duelsWithWinner.length === 0) return;

        const last = duelsWithWinner[duelsWithWinner.length - 1];
        const next = getNextDuel(bracket, last.id);

        const target = next ? duelRefs.current[next.id] : duelRefs.current[last.id];

        if (target) {
            target.scrollIntoView({ behavior: "smooth", block: "center" });
            setHasScrolled(true);

            const highlighted = next ? next.id : last.id;
            setHighlightedDuel(highlighted);

            setTimeout(() => setHighlightedDuel(null), 2000);
        }
    }, [bracket, hasScrolled, getNextDuel]);

    // Reset scroll en changeant de catégorie
    useEffect(() => {
        setHasScrolled(false);
    }, [category]);

    // -------------------------
    // Détection rounds terminés
    // -------------------------
    useEffect(() => {
        if (!bracket?.rounds) return;

        bracket.rounds.forEach((round, index) => {
            const allHaveWinner = round.every((duel) => duel.winner);

            if (allHaveWinner && !roundCompletionTimes[index]) {
                setRoundCompletionTimes((prev) => ({ ...prev, [index]: Date.now() }));
            }
        });
    }, [bracket, roundCompletionTimes]);

    // -------------------------
    // Après 1 minute → scroll round suivant — SPEAKER UNIQUEMENT.
    // -------------------------
    useEffect(() => {
        if (!speakerMode) return;
        if (!bracket?.rounds) return;

        Object.entries(roundCompletionTimes).forEach(([roundIndex, timestamp]) => {
            if (Date.now() - timestamp > 60000) {
                const nextRoundIndex = parseInt(roundIndex, 10) + 1;

                if (bracket.rounds[nextRoundIndex]) {
                    const firstDuel = bracket.rounds[nextRoundIndex][0];
                    const element = duelRefs.current[firstDuel.id];

                    if (element) {
                        element.scrollIntoView({ behavior: "smooth", block: "center" });
                    }

                    setRoundCompletionTimes((prev) => {
                        const updated = { ...prev };
                        delete updated[roundIndex];
                        return updated;
                    });
                }
            }
        });
    }, [roundCompletionTimes, bracket]);

    const switchCategory = (cat) => {
        navigate(`/parallel-live/${cat}${speakerMode ? "/speaker" : ""}`);
    };

    // Carte de duel — largeur MD partagée par le nombre de colonnes voulu,
    // hauteur FIXE (pas minHeight) pour que toutes les cartes soient de
    // taille égale peu importe leur contenu (photofinish/gagnant affichés
    // Taille FIXE (largeur ET hauteur) — le nombre de cartes par ligne se
    // détermine naturellement selon la largeur d'écran disponible (flexbox
    // qui se replie), plutôt que des colonnes en pourcentage.
    const CARD_WIDTH = speakerMode ? 480 : 340;
    const CARD_HEIGHT = 380;

    const renderDuelCard = (duel) => {
        const A = duel.athleteA;
        const B = duel.athleteB;
        const winner = duel.winner;

        if (!A || !B) return null;

        const animateWinner = lastWinners[duel.id];

        return (
            <Card
                key={duel.id}
                ref={(el) => (duelRefs.current[duel.id] = el)}
                sx={{
                    width: CARD_WIDTH,
                    height: CARD_HEIGHT,
                    flexShrink: 0,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    overflow: "hidden",
                    background: "#f5f5f5",
                    borderLeft: "6px solid #1976d2",
                    transition: "transform 0.4s ease, box-shadow 0.4s ease",
                    ...(highlightedDuel === duel.id && {
                        transform: "scale(1.03)",
                        boxShadow: "0 0 20px rgba(25,118,210,0.4)",
                    }),
                }}
            >
                <CardContent sx={{ overflowY: "auto" }}>
                    <Typography variant="h5" mb={2} fontWeight="bold">
                        {duel.id}
                    </Typography>

                    {/* ATHLETE A */}
                    <Box>
                        <Typography variant="h6">
                            {A.name} {A.nation?.toUpperCase() || ""} (bib {A.bib})
                        </Typography>
                        {duel.colorA && (
                            <Chip
                                label={duel.colorA.toUpperCase()}
                                color={duel.colorA === "blue" ? "primary" : "error"}
                                sx={{ mt: 1 }}
                            />
                        )}
                    </Box>

                    {/* ATHLETE B */}
                    <Box mt={2}>
                        <Typography variant="h6">
                            {B.name} {B.nation?.toUpperCase() || ""} (bib {B.bib})
                        </Typography>
                        {duel.colorB && (
                            <Chip
                                label={duel.colorB.toUpperCase()}
                                color={duel.colorB === "blue" ? "primary" : "error"}
                                sx={{ mt: 1 }}
                            />
                        )}
                    </Box>

                    {/* PHOTOFINISH */}
                    {duel.photoFinish && (
                        <Box
                            mt={2}
                            sx={{
                                background: "#fff3cd",
                                border: "1px solid #ffeeba",
                                padding: "8px",
                                borderRadius: "6px",
                            }}
                        >
                            <Typography variant="h6" color="warning.main" fontWeight="bold">
                                ⚠️ Photofinish necessary
                            </Typography>
                        </Box>
                    )}

                    {/* JURY DECISION */}
                    {duel.juryDecision && (
                        <Box
                            mt={2}
                            sx={{
                                background: "#e3f2fd",
                                border: "1px solid #90caf9",
                                padding: "8px",
                                borderRadius: "6px",
                            }}
                        >
                            <Typography variant="h6" color="info.main" fontWeight="bold">
                                ⚖️ Jury decision
                            </Typography>
                        </Box>
                    )}

                    {/* WINNER */}
                    {winner && (
                        <Fade in={animateWinner} timeout={600}>
                            <Box mt={3} sx={{ background: "#d0f0d0", padding: "8px", borderRadius: "6px" }}>
                                <Typography variant="h6" color="success.main" fontWeight="bold">
                                    → Winner : {winner.name} {winner.nation?.toUpperCase() || ""}
                                </Typography>
                            </Box>
                        </Fade>
                    )}
                </CardContent>
            </Card>
        );
    };

    // -------------------------
    // Rendu
    // -------------------------
    if (!bracket || !bracket.rounds) {
        return (
            <Box p={3} textAlign="center">
                {/* Sous-navigation : visible seulement par le chronométreur connecté */}
                <Box sx={{ textAlign: "left" }}>
                    <ParallelSubNav category={category} current="live" visibleFor={["chronometreur"]} />
                </Box>
                <Typography variant="h4" color="text.secondary">
                    Qualification in progress
                </Typography>
                <Typography variant="body1" mt={2}>
                    Brackets will be available once the bracket has been generated.
                </Typography>
            </Box>
        );
    }

    const allDuels = bracket.rounds.flat();
    const duelsWithWinner = allDuels.filter((d) => d.winner);
    const currentRoundIndex =
        duelsWithWinner.length === 0
            ? 0
            : bracket.rounds.findIndex((round) =>
                round.some((d) => d.id === duelsWithWinner[duelsWithWinner.length - 1].id)
            );

    const fontScale = speakerMode ? 1.25 : 1;

    return (
        <Box
            sx={{
                background: "#ffffff",
                padding: "20px",
                maxWidth: "1600px",
                margin: "0 auto",
                fontSize: `${1.3 * fontScale}rem`,
            }}
        >
            {/* Sous-navigation des pages de la course — affichée uniquement
                pour le chronométreur connecté (jamais sur l'écran public,
                ni pour les rôles couleurs / parallel timer). */}
            <Box sx={{ fontSize: "1rem" }}>
                <ParallelSubNav category={category} current="live" visibleFor={["chronometreur"]} />
            </Box>

            {/* Sélecteur de catégorie */}
            {categories.length > 1 && (
                <Box textAlign="center" mb={3}>
                    <ButtonGroup variant="outlined" color="secondary">
                        {categories.map((cat) => (
                            <Button
                                key={cat}
                                variant={cat === category ? "contained" : "outlined"}
                                onClick={() => switchCategory(cat)}
                            >
                                {cat}
                            </Button>
                        ))}
                    </ButtonGroup>
                </Box>
            )}


            {/* MINI TIMELINE */}
            <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mb: 4, flexWrap: "wrap" }}>
                {bracket.rounds.map((round, index) => {
                    const label = translateStage(round[0]?.label) || `Round ${index + 1}`;
                    const isPast = round.every((d) => d.winner);
                    const isCurrent = index === currentRoundIndex;

                    return (
                        <Box
                            key={index}
                            onClick={() => {
                                const first = round[0];
                                const el = duelRefs.current[first.id];
                                if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
                            }}
                            sx={{
                                padding: "8px 16px",
                                borderRadius: "20px",
                                cursor: "pointer",
                                fontWeight: "bold",
                                transition: "0.3s",
                                background: isCurrent ? "#1976d2" : isPast ? "#4caf50" : "#bdbdbd",
                                color: "white",
                                "&:hover": { transform: "scale(1.05)" },
                            }}
                        >
                            {label}
                        </Box>
                    );
                })}

                {bracket.smallFinal && (
                    <Box
                        onClick={() => {
                            const el = duelRefs.current[bracket.smallFinal.id];
                            if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
                        }}
                        sx={{
                            padding: "8px 16px",
                            borderRadius: "20px",
                            cursor: "pointer",
                            fontWeight: "bold",
                            transition: "0.3s",
                            background: bracket.smallFinal.winner ? "#4caf50" : "#bdbdbd",
                            color: "white",
                            "&:hover": { transform: "scale(1.05)" },
                        }}
                    >
                        Small Final
                    </Box>
                )}
            </Box>

            <Typography variant="h3" mb={4} textAlign="center" fontWeight="bold">
                Brackets Finals – {category}
            </Typography>

            {bracket.rounds.map((round, roundIndex) => (
                <Box key={roundIndex} mb={5}>
                    <Typography
                        variant="h4"
                        mb={3}
                        textAlign="center"
                        sx={{ textTransform: "uppercase", fontWeight: "bold" }}
                    >
                        {translateStage(round[0]?.label)}
                    </Typography>

                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3, justifyContent: "center" }}>
                        {round.map((duel) => renderDuelCard(duel))}
                    </Box>
                </Box>
            ))}

            {bracket.smallFinal && (
                <Box mb={5}>
                    <Typography
                        variant="h4"
                        mb={3}
                        textAlign="center"
                        sx={{ textTransform: "uppercase", fontWeight: "bold" }}
                    >
                        Small Final
                    </Typography>

                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3, justifyContent: "center" }}>
                        {renderDuelCard(bracket.smallFinal)}
                    </Box>
                </Box>
            )}
        </Box>
    );
}